/**
 * VH-83: Recipe capture page (multi-mode tabbed UI).
 *
 * Three modes:
 *  - manual: drives CaptureManualForm, posts to POST /recipes
 *  - ai-generate: prompt → draft → review → confirm → POST /recipes
 *  - cookbook-photo: upload → draft → review → confirm → POST /recipes
 *
 * A single "draft" state object (when set) overrides the form with the
 * AI/photo draft, and renders RecipeDraftReview.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Tab } from '@headlessui/react'
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  SparklesIcon,
  CameraIcon,
} from '@heroicons/react/24/outline'
import toast, { Toaster } from 'react-hot-toast'
import {
  useCreateRecipe,
  useAIGenerateDraft,
  usePhotoDraft,
  useRecipeCategories,
} from '../hooks/useRecipes'
import CaptureManualForm from '../components/recipes/CaptureManualForm'
import CaptureAIPromptForm from '../components/recipes/CaptureAIPromptForm'
import CapturePhotoUploader from '../components/recipes/CapturePhotoUploader'
import RecipeDraftReview from '../components/recipes/RecipeDraftReview'
import clsx from 'clsx'
import { contentLanguage } from '../i18n/supported'

const TABS = [
  { id: 'manual', icon: PencilSquareIcon, key: 'recipes.capture.tabManual' },
  { id: 'ai', icon: SparklesIcon, key: 'recipes.capture.tabAI' },
  { id: 'photo', icon: CameraIcon, key: 'recipes.capture.tabPhoto' },
]

export default function RecipeCapture() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const lang = contentLanguage(i18n?.language)

  const { data: categories = [] } = useRecipeCategories(lang)
  const createMutation = useCreateRecipe()
  const aiMutation = useAIGenerateDraft()
  const photoMutation = usePhotoDraft()

  const [draft, setDraft] = useState(null)
  const [aiError, setAiError] = useState(null)
  const [photoError, setPhotoError] = useState(null)

  const handleManualSubmit = async (payload) => {
    try {
      const created = await createMutation.mutateAsync(payload)
      toast.success(t('recipes.capture.savedToast'))
      if (created?.id) navigate(`/recipes/${created.id}`)
      else navigate('/recipes')
    } catch (err) {
       
      console.error('[RecipeCapture] save failed', err)
      toast.error(t('recipes.capture.saveFailed'))
    }
  }

  const handleAIGenerate = async (payload) => {
    setAiError(null)
    try {
      const result = await aiMutation.mutateAsync(payload)
      setDraft({ ...result, _draftId: `ai-${Date.now()}` })
    } catch (err) {
       
      console.error('[RecipeCapture] ai-generate failed', err)
      const status = err?.response?.status
      if (status === 429) {
        setAiError(t('recipes.ai.rateLimited'))
      } else if (status === 422) {
        // Pydantic validation error — surface the most useful detail rather
        // than the generic "Adjust the prompt" toast (e.g. when the prompt
        // exceeds the server-side max_length).
        const detail = err?.response?.data?.detail
        let message = null
        if (Array.isArray(detail) && detail.length > 0) {
          const d = detail[0]
          const field = (d.loc || []).slice(1).join('.') || 'request'
          message = `${field}: ${d.msg}`
        } else if (typeof detail === 'string') {
          message = detail
        }
        setAiError(message || t('recipes.ai.errorGenerate'))
      } else {
        setAiError(t('recipes.ai.errorGenerate'))
      }
    }
  }

  const handlePhotoAnalyze = async (formData) => {
    setPhotoError(null)
    try {
      const result = await photoMutation.mutateAsync(formData)
      setDraft({ ...result, _draftId: `photo-${Date.now()}` })
    } catch (err) {
       
      console.error('[RecipeCapture] from-photo failed', err)
      setPhotoError(t('recipes.photo.errorAnalyze'))
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="recipe-capture-page">
      <Toaster position="top-right" />
      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate('/recipes')}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t('recipes.actions.back')}
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mt-2">
          {t('recipes.capture.title')}
        </h1>
        <p className="mt-1 text-gray-600">{t('recipes.capture.subtitle')}</p>
      </div>

      {draft ? (
        <div className="card">
          <div className="card-body">
            <RecipeDraftReview
              draft={draft}
              categories={categories}
              onSubmit={handleManualSubmit}
              onDiscard={() => setDraft(null)}
              submitting={createMutation.isPending}
            />
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <Tab.Group>
              <Tab.List className="flex gap-2 border-b border-gray-200 mb-4">
                {TABS.map((tab) => (
                  <Tab
                    key={tab.id}
                    className={({ selected }) =>
                      clsx(
                        'flex items-center gap-1 px-4 py-2 text-sm font-medium border-b-2 -mb-px focus:outline-none',
                        selected
                          ? 'border-primary-500 text-primary-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      )
                    }
                    data-testid={`tab-${tab.id}`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {t(tab.key)}
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels>
                <Tab.Panel>
                  <CaptureManualForm
                    categories={categories}
                    onSubmit={handleManualSubmit}
                    submitting={createMutation.isPending}
                  />
                </Tab.Panel>
                <Tab.Panel>
                  <CaptureAIPromptForm
                    onGenerate={handleAIGenerate}
                    generating={aiMutation.isPending}
                    error={aiError}
                  />
                </Tab.Panel>
                <Tab.Panel>
                  <CapturePhotoUploader
                    onAnalyze={handlePhotoAnalyze}
                    analyzing={photoMutation.isPending}
                    error={photoError}
                  />
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </div>
        </div>
      )}
    </div>
  )
}
